/**
 * 웹이 직접 그리는 영어 달력 — 날짜(yyyy-mm-dd)·날짜시각(yyyy-mm-dd HH:mm:ss) 칸의 고르개.
 *
 * 사장님 2026-10-02 「1」 — 「관리자 웹이 직접 그리는 영어 달력으로 바꿉니다. 어느 컴퓨터에서나 영어로 나옵니다.」. 그 전에는
 * 달력 단추가 브라우저 고르개(숨은 브라우저 날짜 칸의 `showPicker`)를 열어 컴퓨터 언어를 따랐다(한국어 컴퓨터에서는
 * 「2026년 10월 / 일 월 화 …」). 이제 브라우저 고르개와 숨은 날짜 칸은 쓰지 않는다.
 *
 * - 라이브러리 없이 이 파일 하나다(Date 로 셈, 시간대는 관리자 컴퓨터 시간 — format.ts 와 같음).
 * - 글은 영어로 고정한다 — 달 이름·요일·단추를 여기 적고 `toLocaleString` 을 쓰지 않는다(브라우저 언어를 따르지 않게).
 * - 한 주는 일요일부터(Sun ~ Sat). 달마다 6주 줄을 늘 그린다(창 높이가 달마다 바뀌지 않게).
 * - 날짜 칸: 날을 누르면 칸에 넣고 닫는다. 날짜시각 칸: 날·시·분·초를 고를 때마다 칸에 넣고, 「Done」으로 닫는다.
 * - 키보드(WAI-ARIA 날짜 고르개 꼴): ←→ 하루, ↑↓ 한 주, Home/End 그 주의 일요일·토요일, PageUp/PageDown 한 달(Shift 는
 *   한 해), Enter·Space 고르기, Esc 닫기(초점은 달력 단추로), Tab 은 창 안에서 돈다.
 * - 자리: 단추 아래(모자라면 위), 화면 밖으로 넘치지 않게 좌우를 맞춘다(fixed — 확인 창 안에서도 잘리지 않음). 폭은 휴대폰
 *   너비에서 화면보다 넓어지지 않는다. 바깥을 누르면 닫는다.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react'

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

/** 달력의 하루(달 `m` 은 0~11). */
export interface Ymd { y: number; m: number; d: number }
/** 시각(0~23·0~59·0~59). */
export interface Hms { h: number; mi: number; s: number }

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0')
}

/** 컴퓨터 시간대의 그날 00:00 — 넘친 날·달은 Date 가 다음 달·해로 넘긴다. */
function at(y: number, m: number, d: number): Date {
  const t = new Date(2000, 0, 1)
  // ⚠ setFullYear 로 넣는다 — new Date(99, …) 는 1999 년이 된다.
  t.setFullYear(y, m, d)
  t.setHours(0, 0, 0, 0)
  return t
}

function fromDate(t: Date): Ymd {
  return { y: t.getFullYear(), m: t.getMonth(), d: t.getDate() }
}

export function addDays(v: Ymd, n: number): Ymd {
  return fromDate(at(v.y, v.m, v.d + n))
}

function daysIn(y: number, m: number): number {
  return at(y, m + 1, 0).getDate()
}

/** 몇 달 앞뒤의 같은 날(그 달에 없으면 그 달 마지막 날). */
export function addMonths(v: Ymd, n: number): Ymd {
  const first = at(v.y, v.m + n, 1)
  const y = first.getFullYear()
  const m = first.getMonth()
  return { y, m, d: Math.min(v.d, daysIn(y, m)) }
}

function weekday(v: Ymd): number {
  return at(v.y, v.m, v.d).getDay()
}

function same(a: Ymd | null, b: Ymd | null): boolean {
  return a !== null && b !== null && a.y === b.y && a.m === b.m && a.d === b.d
}

/** 「yyyy-mm-dd」. */
export function dayText(v: Ymd): string {
  return `${pad(v.y, 4)}-${pad(v.m + 1)}-${pad(v.d)}`
}

/** 「HH:mm:ss」. */
export function timeText(t: Hms): string {
  return `${pad(t.h)}:${pad(t.mi)}:${pad(t.s)}`
}

/** 칸 글 앞의 「yyyy-mm-dd」 → 하루. 없는 날(2026-02-30)·모양이 아니면 null. */
export function parseDay(text: string): Ymd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text.trim())
  if (!m) return null
  const v = { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) }
  return same(fromDate(at(v.y, v.m, v.d)), v) ? v : null
}

/** 칸 글 「yyyy-mm-dd HH:mm:ss」의 시각. 모양이 아니거나 넘친 값(24:00:00)이면 null. */
export function parseTime(text: string): Hms | null {
  const m = /^\d{4}-\d{2}-\d{2} (\d{2}):(\d{2}):(\d{2})$/.exec(text.trim())
  if (!m) return null
  const t = { h: Number(m[1]), mi: Number(m[2]), s: Number(m[3]) }
  return t.h < 24 && t.mi < 60 && t.s < 60 ? t : null
}

/** 그 달의 6주(일요일부터) — 빈 칸은 null. */
function weeksOf(y: number, m: number): (Ymd | null)[][] {
  const lead = at(y, m, 1).getDay()
  const total = daysIn(y, m)
  const cells: (Ymd | null)[] = []
  for (let i = 0; i < 42; i++) {
    const d = i - lead + 1
    cells.push(d >= 1 && d <= total ? { y, m, d } : null)
  }
  return Array.from({ length: 6 }, (_, w) => cells.slice(w * 7, w * 7 + 7))
}

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const SIXTY = Array.from({ length: 60 }, (_, i) => i)

const NAV = 'rounded px-2 py-1 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
const ACTION = 'rounded border border-slate-300 px-2.5 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800'
const SELECT = 'rounded border border-slate-300 px-1 py-0.5 font-mono dark:border-slate-600 dark:bg-slate-800'

/**
 * 달력 창. `value` 는 지금 칸의 글(읽히면 그 날·시각에서 연다), `onPick` 은 칸에 넣을 글(「Clear」면 빈 글), `onClose` 의
 * `refocus` 가 true 면 부르는 쪽이 달력 단추로 초점을 돌린다.
 */
export function Calendar({ kind, value, anchor, onPick, onClose, clearable }: {
  kind: 'date' | 'datetime'
  value: string
  /** 달력 단추 — 자리를 잡는 기준, 바깥 누름에서 빼는 곳. */
  anchor: RefObject<HTMLElement | null>
  onPick: (text: string) => void
  onClose: (refocus: boolean) => void
  /** 「Clear」를 둘지(빈 값을 받는 칸만). */
  clearable: boolean
}) {
  const box = useRef<HTMLDivElement>(null)
  const [today] = useState<Ymd>(() => fromDate(new Date()))
  const [selected, setSelected] = useState<Ymd | null>(() => parseDay(value))
  const [time, setTime] = useState<Hms>(() => parseTime(value) ?? { h: 0, mi: 0, s: 0 })
  const [focus, setFocus] = useState<Ymd>(() => parseDay(value) ?? today)
  // 키보드로 날을 옮겼을 때만 그 날로 초점을 옮긴다(달 단추를 누를 때는 단추에 둔다).
  const moveFocus = useRef(false)
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  const [, setTick] = useState(0)

  // 창의 실제 크기(처음 한 번) — 자리 잡기에 쓴다.
  useLayoutEffect(() => {
    const b = box.current
    if (b && b.offsetWidth > 0) setSize({ w: b.offsetWidth, h: b.offsetHeight })
  }, [])

  // 화면 크기·스크롤이 바뀌면 자리를 다시 잡는다. 바깥을 누르면 닫는다.
  useEffect(() => {
    const again = () => setTick((t) => t + 1)
    const outside = (e: Event) => {
      const t = e.target as Node | null
      if (!t || box.current?.contains(t) || anchor.current?.contains(t)) return
      onClose(false)
    }
    window.addEventListener('resize', again)
    window.addEventListener('scroll', again, true)
    document.addEventListener('mousedown', outside, true)
    document.addEventListener('touchstart', outside, true)
    return () => {
      window.removeEventListener('resize', again)
      window.removeEventListener('scroll', again, true)
      document.removeEventListener('mousedown', outside, true)
      document.removeEventListener('touchstart', outside, true)
    }
  }, [anchor, onClose])

  // 열 때 고른 날(없으면 오늘)에 초점, 키보드로 옮긴 뒤에는 옮긴 날에 초점.
  const focusKey = dayText(focus)
  const opened = useRef(false)
  useEffect(() => {
    if (opened.current && !moveFocus.current) return
    opened.current = true
    moveFocus.current = false
    box.current?.querySelector<HTMLButtonElement>(`button[data-day="${focusKey}"]`)?.focus()
  }, [focusKey])

  function emit(day: Ymd, t: Hms) {
    onPick(kind === 'date' ? dayText(day) : `${dayText(day)} ${timeText(t)}`)
  }

  function pick(day: Ymd) {
    setSelected(day)
    setFocus(day)
    emit(day, time)
    if (kind === 'date') onClose(true)
  }

  function changeTime(next: Hms) {
    setTime(next)
    if (selected) emit(selected, next)
  }

  function showMonth(n: number) {
    setFocus(addMonths(focus, n))
  }

  function onDayKey(e: KeyboardEvent<HTMLButtonElement>, day: Ymd) {
    let next: Ymd
    switch (e.key) {
      case 'ArrowLeft': next = addDays(day, -1); break
      case 'ArrowRight': next = addDays(day, 1); break
      case 'ArrowUp': next = addDays(day, -7); break
      case 'ArrowDown': next = addDays(day, 7); break
      case 'Home': next = addDays(day, -weekday(day)); break
      case 'End': next = addDays(day, 6 - weekday(day)); break
      case 'PageUp': next = addMonths(day, e.shiftKey ? -12 : -1); break
      case 'PageDown': next = addMonths(day, e.shiftKey ? 12 : 1); break
      default: return
    }
    e.preventDefault()
    moveFocus.current = true
    setFocus(next)
  }

  function onBoxKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onClose(true)
      return
    }
    if (e.key !== 'Tab' || !box.current) return
    const items = [...box.current.querySelectorAll<HTMLElement>('button:not([tabindex="-1"]), select')]
    const first = items[0]
    const last = items[items.length - 1]
    if (!first || !last) return
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }

  // 자리: 단추 아래, 모자라면 위. 좌우는 단추 오른쪽 끝에 맞추되 화면 안으로.
  const vw = typeof window === 'undefined' ? 1024 : window.innerWidth
  const vh = typeof window === 'undefined' ? 768 : window.innerHeight
  const a = anchor.current?.getBoundingClientRect()
  const w = size?.w ?? Math.min(288, vw - 16)
  const h = size?.h ?? (kind === 'datetime' ? 380 : 340)
  let left = 8
  let top = 8
  if (a) {
    left = Math.max(8, Math.min(a.right - w, vw - w - 8))
    top = a.bottom + 4
    if (top + h > vh - 8 && a.top - h - 4 >= 8) top = a.top - h - 4
  }

  const label = kind === 'date' ? 'Choose date' : 'Choose date and time'
  const month = `${MONTHS[focus.m]} ${focus.y}`

  return (
    <div ref={box} role="dialog" aria-modal="true" aria-label={label} onKeyDown={onBoxKey}
      style={{ top, left }}
      className="fixed z-[60] w-72 max-w-[calc(100vw-1rem)] rounded border border-slate-300 bg-white p-3 text-sm text-slate-800 shadow-lg dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" className={NAV} aria-label="Previous month" onClick={() => showMonth(-1)}>‹</button>
        <span className="font-semibold" aria-live="polite">{month}</span>
        <button type="button" className={NAV} aria-label="Next month" onClick={() => showMonth(1)}>›</button>
      </div>
      <table role="grid" aria-label={month} className="w-full table-fixed border-collapse text-center text-xs">
        <thead>
          <tr>
            {WEEKDAYS.map((wd, i) => (
              <th key={wd} scope="col" abbr={WEEKDAYS_LONG[i]} className="pb-1 font-medium text-slate-500">{wd}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeksOf(focus.y, focus.m).map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => (
                <td key={di} className="p-0.5">
                  {/* 빈 칸도 날 칸과 같은 높이 — 6주 줄이 늘 같은 높이라 달을 넘겨도 창이 줄거나 늘지 않는다. */}
                  {!day && <span className="block h-8" aria-hidden="true" />}
                  {day && (
                    <button type="button" data-day={dayText(day)}
                      tabIndex={same(day, focus) ? 0 : -1}
                      aria-label={`${WEEKDAYS_LONG[di]}, ${MONTHS[day.m]} ${day.d}, ${day.y}`}
                      aria-pressed={same(day, selected)}
                      aria-current={same(day, today) ? 'date' : undefined}
                      onClick={() => pick(day)}
                      onKeyDown={(e) => onDayKey(e, day)}
                      className={`h-8 w-full rounded tabular-nums ${
                        same(day, selected)
                          ? 'bg-teal-800 font-semibold text-white'
                          : same(day, today)
                            ? 'font-semibold text-teal-800 ring-1 ring-teal-700 dark:text-teal-300'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}>
                      {day.d}
                    </button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {kind === 'datetime' && (
        <div className="mt-2 flex items-center gap-1 text-xs">
          <span className="mr-1 font-medium text-slate-500">Time</span>
          <select aria-label="Hour" className={SELECT} value={time.h}
            onChange={(e) => changeTime({ ...time, h: Number(e.target.value) })}>
            {HOURS.map((n) => <option key={n} value={n}>{pad(n)}</option>)}
          </select>
          <span>:</span>
          <select aria-label="Minute" className={SELECT} value={time.mi}
            onChange={(e) => changeTime({ ...time, mi: Number(e.target.value) })}>
            {SIXTY.map((n) => <option key={n} value={n}>{pad(n)}</option>)}
          </select>
          <span>:</span>
          <select aria-label="Second" className={SELECT} value={time.s}
            onChange={(e) => changeTime({ ...time, s: Number(e.target.value) })}>
            {SIXTY.map((n) => <option key={n} value={n}>{pad(n)}</option>)}
          </select>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 text-xs">
        {clearable
          ? <button type="button" className={ACTION} onClick={() => { onPick(''); onClose(true) }}>Clear</button>
          : <span />}
        <span className="flex gap-2">
          <button type="button" className={ACTION} onClick={() => pick(today)}>Today</button>
          {kind === 'datetime' && (
            <button type="button" className={`${ACTION} border-teal-800 bg-teal-800 text-white hover:bg-teal-700`}
              onClick={() => onClose(true)}>Done</button>
          )}
        </span>
      </div>
    </div>
  )
}
